import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Td,
  Tr,
  Text
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FaChartLine, FaCoins, FaUsers } from "react-icons/fa";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from "recharts";
import { useAuth } from "../../../context/AuthContext";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  convertLegacyRole,
  convertLegacyRoleWithContext,
  isBUAdmin,
  isCompanyAdmin,
  isManagerRole,
  isRecruiterRole,
  isSalesRole
} from "../../../constants/roles";
import {
  getMyPerformance,
  getTeamPerformance,
  getFinancialKpis,
  getPortfolioAnalysis,
} from "../../../api/performanceApi";
import {
  PageHeader,
  Panel,
  DataTable,
  KpiStrip,
  KpiCard,
  EmptyState,
} from "../../../components/Portal/ui";

// Corporate Azure palette (Recharts can't read Chakra tokens — inline hex).
const CHART_NAVY = "#153B69";
const CHART_NAVY_3 = "#1E4D78";
const CHART_MINT = "#1BAE9A";
const CHART_SELECTED = "#1BAE9A";
const CHART_LINE = "#1E4D78";
const CHART_GRID = "#D6E0EA";
const CHART_AXIS = "#617386";

// Compact filter Select matching the template's 31px controls.
const filterSelectSx = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "31px !important",
  fontSize: "12px",
  fontWeight: 600,
};

const toDateInputValue = (d) => {
  if (!d) return "";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (!(dt instanceof Date) || Number.isNaN(dt.getTime())) return "";
    return dt.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 6);
  return { fromDate: toDateInputValue(from), toDate: toDateInputValue(to) };
};

const sanitizeBucket = (value) => (["week", "month", "year"].includes(value) ? value : "month");

const sanitizeRoleScope = (value) =>
  ["all", "sales", "recruiter"].includes(value) ? value : "all";

const parseUserIds = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const safeNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const bucketLabel = (b, idx) => {
  if (!b || typeof b !== "object") return `#${idx + 1}`;
  return (
    b.label ||
    b.key ||
    b.bucket ||
    b.period ||
    b.periodLabel ||
    b.yearWeek ||
    b.year_week ||
    b.yearMonth ||
    b.year_month ||
    (b.year && b.month ? `${b.year}-${String(b.month).padStart(2, "0")}` : null) ||
    (b.year && b.week ? `${b.year}-W${String(b.week).padStart(2, "0")}` : null) ||
    b.year ||
    `#${idx + 1}`
  );
};

const normalizeRate = (value) => {
  const v = safeNumber(value);
  if (!Number.isFinite(v)) return 0;
  return v > 1 ? v / 100 : v;
};

const resolveConversionRate = ({
  directValue,
  numerator,
  denominator,
  numeratorFallback,
  denominatorFallback
}) => {
  if (Number.isFinite(Number(directValue))) {
    return normalizeRate(directValue);
  }
  const num = safeNumber(numerator);
  const den = safeNumber(denominator);
  if (den > 0) return num / den;
  if (denominatorFallback > 0) return numeratorFallback / denominatorFallback;
  return 0;
};

const getOpportunitiesTotal = (b, offMarket, referral) => {
  const total = safeNumber(b?.opportunitiesTotal);
  return total || offMarket + referral;
};

const buildSalesMetrics = (b) => {
  const totalOpenRequirements = safeNumber(b?.totalOpenRequirements);
  const newOpenRequirements = safeNumber(b?.newOpenRequirements);
  const coveredRequirements = safeNumber(b?.coveredRequirements);
  const profilesPresented = safeNumber(b?.profilesPresented);
  const wonRequirements = safeNumber(b?.wonRequirements);
  const offMarketOpportunities = safeNumber(b?.offMarketOpportunities);
  const referralOpportunities = safeNumber(b?.referralOpportunities);
  const opportunitiesTotal = getOpportunitiesTotal(b, offMarketOpportunities, referralOpportunities);
  const recruiterProfilesReceived = safeNumber(b?.recruiterProfilesReceived);
  const avgAsapSkillsScoreProposed = safeNumber(b?.avgAsapSkillsScoreProposed);
  const candidatesSubmitted = safeNumber(b?.candidatesSubmitted);
  const clientInterviews = safeNumber(b?.clientInterviews);
  const wins = safeNumber(b?.wins);
  const conversionOppToInterview = resolveConversionRate({
    directValue: b?.conversionOppToInterview,
    numerator: b?.oppToInterviewNumerator,
    denominator: b?.oppToInterviewDenominator,
    numeratorFallback: clientInterviews,
    denominatorFallback: opportunitiesTotal
  });
  const conversionOppToWin = resolveConversionRate({
    directValue: b?.conversionOppToWin,
    numerator: b?.oppToWinNumerator,
    denominator: b?.oppToWinDenominator,
    numeratorFallback: wins,
    denominatorFallback: opportunitiesTotal
  });
  return {
    totalOpenRequirements,
    newOpenRequirements,
    coveredRequirements,
    profilesPresented,
    wonRequirements,
    offMarketOpportunities,
    referralOpportunities,
    opportunitiesTotal,
    recruiterProfilesReceived,
    avgAsapSkillsScoreProposed,
    candidatesSubmitted,
    clientInterviews,
    wins,
    conversionOppToInterview,
    conversionOppToWin
  };
};

const buildRecruiterMetrics = (b) => {
  const staffingNeedsToCover = safeNumber(b?.staffingNeedsToCover);
  const profilesProposed = safeNumber(b?.profilesProposed);
  const avgAtsScore = safeNumber(b?.avgAtsScore);
  const avgAsapSkillsScoreProposed = safeNumber(b?.avgAsapSkillsScoreProposed);
  const leadTimeOppToFirstProfile = safeNumber(b?.leadTimeOppToFirstProfile);
  return {
    staffingNeedsToCover,
    profilesProposed,
    avgAtsScore,
    avgAsapSkillsScoreProposed,
    leadTimeOppToFirstProfile
  };
};

const buildBucket = (b, idx) => ({
  label: bucketLabel(b, idx),
  ...buildSalesMetrics(b),
  ...buildRecruiterMetrics(b)
});

const summarizeBuckets = (buckets = []) => {
  const summary = {
    totalOpenRequirements: 0,
    newOpenRequirements: 0,
    coveredRequirements: 0,
    profilesPresented: 0,
    wonRequirements: 0,
    offMarketOpportunities: 0,
    referralOpportunities: 0,
    opportunitiesTotal: 0,
    recruiterProfilesReceived: 0,
    candidatesSubmitted: 0,
    clientInterviews: 0,
    wins: 0,
    staffingNeedsToCover: 0,
    profilesProposed: 0,
    oppToInterviewNumerator: 0,
    oppToInterviewDenominator: 0,
    oppToWinNumerator: 0,
    oppToWinDenominator: 0,
    _asapSum: 0,
    _asapCount: 0,
    _atsSum: 0,
    _atsCount: 0,
    _leadSum: 0,
    _leadCount: 0
  };

  buckets.forEach((b) => {
    summary.totalOpenRequirements += safeNumber(b?.totalOpenRequirements);
    summary.newOpenRequirements += safeNumber(b?.newOpenRequirements);
    summary.coveredRequirements += safeNumber(b?.coveredRequirements);
    summary.profilesPresented += safeNumber(b?.profilesPresented);
    summary.wonRequirements += safeNumber(b?.wonRequirements);
    summary.offMarketOpportunities += safeNumber(b?.offMarketOpportunities);
    summary.referralOpportunities += safeNumber(b?.referralOpportunities);
    summary.opportunitiesTotal += safeNumber(b?.opportunitiesTotal);
    summary.recruiterProfilesReceived += safeNumber(b?.recruiterProfilesReceived);
    summary.candidatesSubmitted += safeNumber(b?.candidatesSubmitted);
    summary.clientInterviews += safeNumber(b?.clientInterviews);
    summary.wins += safeNumber(b?.wins);
    summary.staffingNeedsToCover += safeNumber(b?.staffingNeedsToCover);
    summary.profilesProposed += safeNumber(b?.profilesProposed);
    summary.oppToInterviewNumerator += safeNumber(b?.oppToInterviewNumerator);
    summary.oppToInterviewDenominator += safeNumber(b?.oppToInterviewDenominator);
    summary.oppToWinNumerator += safeNumber(b?.oppToWinNumerator);
    summary.oppToWinDenominator += safeNumber(b?.oppToWinDenominator);
    const asap = safeNumber(b?.avgAsapSkillsScoreProposed);
    if (asap) {
      summary._asapSum += asap;
      summary._asapCount += 1;
    }
    const ats = safeNumber(b?.avgAtsScore);
    if (ats) {
      summary._atsSum += ats;
      summary._atsCount += 1;
    }
    const lead = safeNumber(b?.leadTimeOppToFirstProfile);
    if (lead) {
      summary._leadSum += lead;
      summary._leadCount += 1;
    }
  });

  const opportunitiesTotal =
    summary.opportunitiesTotal || summary.offMarketOpportunities + summary.referralOpportunities;

  const conversionOppToInterview = resolveConversionRate({
    numerator: summary.oppToInterviewNumerator,
    denominator: summary.oppToInterviewDenominator,
    numeratorFallback: summary.clientInterviews,
    denominatorFallback: opportunitiesTotal
  });
  const conversionOppToWin = resolveConversionRate({
    numerator: summary.oppToWinNumerator,
    denominator: summary.oppToWinDenominator,
    numeratorFallback: summary.wins,
    denominatorFallback: opportunitiesTotal
  });

  return {
    ...summary,
    opportunitiesTotal,
    avgAsapSkillsScoreProposed: summary._asapCount ? summary._asapSum / summary._asapCount : 0,
    avgAtsScore: summary._atsCount ? summary._atsSum / summary._atsCount : 0,
    leadTimeOppToFirstProfile: summary._leadCount ? summary._leadSum / summary._leadCount : 0,
    conversionOppToInterview,
    conversionOppToWin
  };
};

const buildAggregateBuckets = (users = []) => {
  const bucketCount = Math.max(...users.map((u) => u.buckets.length), 0);
  return Array.from({ length: bucketCount }, (_, idx) => {
    const bucketGroup = users
      .map((u) => u.buckets[idx])
      .filter((bucket) => bucket && typeof bucket === "object");
    const label = bucketGroup.length ? bucketLabel(bucketGroup[0], idx) : `#${idx + 1}`;
    return { label, ...summarizeBuckets(bucketGroup) };
  });
};

const formatNumber = (value, decimals = 0) => {
  if (!Number.isFinite(Number(value))) return "-";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  }).format(Number(value));
};

const formatPercent = (value) => `${(normalizeRate(value) * 100).toFixed(1)}%`;

// Recharts tooltip styled to the navy/line tokens.
const rechartsTooltipStyle = {
  fontSize: "12px",
  borderRadius: "10px",
  border: `1px solid ${CHART_GRID}`,
  boxShadow: "0 10px 24px rgba(16,32,51,.08)",
};

const MetricCard = ({ title, children }) => (
  <Box
    bg="portal.panel"
    borderRadius="10px"
    borderWidth="1px"
    borderColor="portal.line"
    boxShadow="kpi"
    p="12px"
  >
    <Text
      textTransform="uppercase"
      fontSize="10.5px"
      fontWeight="800"
      letterSpacing="0.055em"
      color="portal.muted"
      sx={{ marginBottom: "8px !important" }}
    >
      {title}
    </Text>
    {children}
  </Box>
);

const MetricBarChart = ({ data, dataKey, color, selectedIndex, onSelect, valueFormatter }) => (
  <ResponsiveContainer width="100%" height={180}>
    <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_AXIS }} stroke={CHART_GRID} />
      <YAxis tick={{ fontSize: 11, fill: CHART_AXIS }} stroke={CHART_GRID} />
      <RechartsTooltip formatter={(value) => valueFormatter(value)} contentStyle={rechartsTooltipStyle} />
      <Bar
        dataKey={dataKey}
        fill={color}
        radius={[4, 4, 0, 0]}
        onClick={(_, index) => {
          if (typeof onSelect === "function") onSelect(index);
        }}
      >
        {data.map((_, idx) => (
          <Cell key={`cell-${dataKey}-${idx}`} fill={idx === selectedIndex ? CHART_SELECTED : color} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const MetricLineChart = ({
  data,
  dataKey,
  color,
  selectedIndex,
  onSelect,
  valueFormatter,
  yAxisFormatter
}) => (
  <ResponsiveContainer width="100%" height={180}>
    <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_AXIS }} stroke={CHART_GRID} />
      <YAxis tick={{ fontSize: 11, fill: CHART_AXIS }} tickFormatter={yAxisFormatter} stroke={CHART_GRID} />
      <RechartsTooltip formatter={(value) => valueFormatter(value)} contentStyle={rechartsTooltipStyle} />
      <Legend wrapperStyle={{ fontSize: "11px" }} />
      <Line
        type="monotone"
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2}
        dot={{ r: 3, fill: color }}
        activeDot={{ r: 5, fill: CHART_SELECTED }}
        onClick={(_, index) => {
          if (typeof onSelect === "function") onSelect(index);
        }}
      />
    </LineChart>
  </ResponsiveContainer>
);

const Performance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("portal");
  const { user } = useAuth();
  const { businessUnits, currentRole, selectedBusinessUnitId, isLoaded: isBUContextLoaded } =
    useBusinessUnit();

  const initialRange = useMemo(() => {
    const fallbackRange = defaultRange();
    return {
      fromDate: searchParams.get("fromDate") || fallbackRange.fromDate,
      toDate: searchParams.get("toDate") || fallbackRange.toDate
    };
  }, [searchParams]);

  const [bucket, setBucket] = useState(() => sanitizeBucket(searchParams.get("bucket")));
  const [range, setRange] = useState(initialRange);
  const [roleScope, setRoleScope] = useState(() => sanitizeRoleScope(searchParams.get("roleScope")));
  const [search, setSearch] = useState("");
  const [userIds, setUserIds] = useState(() => parseUserIds(searchParams.get("userIds")));

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [team, setTeam] = useState(null);

  // Financial KPIs state
  const [financialData, setFinancialData] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  // Portfolio Analysis state
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  const userRole = useMemo(() => {
    const hasBUs = Boolean(businessUnits && businessUnits.length > 0);
    return convertLegacyRoleWithContext(user?.role, hasBUs);
  }, [businessUnits, user?.role]);

  const buRole = useMemo(() => convertLegacyRole(currentRole), [currentRole]);
  const canSeeTeam = useMemo(
    () => isCompanyAdmin(userRole) || isBUAdmin(buRole) || isManagerRole(buRole),
    [buRole, userRole]
  );

  const activityRole = useMemo(() => {
    if (isSalesRole(buRole)) return "sales";
    if (isRecruiterRole(buRole)) return "recruiter";
    return "sales";
  }, [buRole]);

  const fetchMy = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (!silent) setLoading(true);
      try {
        const params = {
          bucket,
          fromDate: range.fromDate || undefined,
          toDate: range.toDate || undefined
        };
        const res = await getMyPerformance(params, {
          buId: selectedBusinessUnitId || undefined
        });
        setData(res || null);
      } catch {
        setData(null);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [bucket, range.fromDate, range.toDate, selectedBusinessUnitId]
  );

  const fetchTeam = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (!silent) setLoading(true);
      try {
        const params = {
          bucket,
          fromDate: range.fromDate || undefined,
          toDate: range.toDate || undefined,
          roleScope: roleScope || "all",
          userIds: userIds.length ? userIds.join(",") : undefined
        };
        const res = await getTeamPerformance(params, {
          buId: selectedBusinessUnitId || undefined
        });
        setTeam(res || null);
      } catch {
        setTeam(null);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [bucket, range.fromDate, range.toDate, roleScope, selectedBusinessUnitId, userIds]
  );

  useEffect(() => {
    if (!isBUContextLoaded) return;
    if (canSeeTeam) {
      fetchTeam();
    } else {
      fetchMy();
    }
  }, [canSeeTeam, fetchMy, fetchTeam, isBUContextLoaded]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    if (canSeeTeam) fetchTeam();
    else fetchMy();
  }, [isBUContextLoaded, selectedBusinessUnitId, canSeeTeam, fetchMy, fetchTeam]);

  // Fetch Financial KPIs
  const fetchFinancialKpis = useCallback(async () => {
    setFinancialLoading(true);
    try {
      const buId = selectedBusinessUnitId || undefined;
      const result = await getFinancialKpis({}, { buId });
      setFinancialData(result);
    } catch {
      setFinancialData(null);
    } finally {
      setFinancialLoading(false);
    }
  }, [selectedBusinessUnitId]);

  // Fetch Portfolio Analysis
  const fetchPortfolioAnalysis = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const buId = selectedBusinessUnitId || undefined;
      const result = await getPortfolioAnalysis({}, { buId });
      setPortfolioData(result);
    } catch {
      setPortfolioData(null);
    } finally {
      setPortfolioLoading(false);
    }
  }, [selectedBusinessUnitId]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    fetchFinancialKpis();
    if (canSeeTeam) fetchPortfolioAnalysis();
  }, [isBUContextLoaded, selectedBusinessUnitId, canSeeTeam, fetchFinancialKpis, fetchPortfolioAnalysis]);

  const myBuckets = useMemo(() => {
    return Array.isArray(data?.buckets) ? data.buckets : [];
  }, [data]);

  const teamUsers = useMemo(() => {
    const users = Array.isArray(team?.users) ? team.users : [];
    const normalized = users.map((u) => {
      const buckets = Array.isArray(u?.buckets) ? u.buckets : Array.isArray(u?.series) ? u.series : [];
      const summary = summarizeBuckets(buckets);
      const id = u?.id ?? u?.userId ?? u?.user_id ?? u?.user?.id ?? null;
      const firstName = u?.firstName ?? u?.first_name ?? u?.user?.firstName ?? u?.user?.first_name ?? "";
      const lastName = u?.lastName ?? u?.last_name ?? u?.user?.lastName ?? u?.user?.last_name ?? "";
      const fullNameFromParts = `${String(firstName || "").trim()} ${String(lastName || "").trim()}`.trim();
      const name =
        fullNameFromParts ||
        u?.name ||
        u?.fullName ||
        u?.full_name ||
        u?.user?.name ||
        u?.user?.fullName ||
        u?.user?.full_name ||
        u?.email ||
        u?.user?.email ||
        "-";
      const role = u?.role ?? u?.user?.role ?? "-";
      return { raw: u, id, name, role, buckets, summary };
    });
    const q = (search || "").trim().toLowerCase();
    return q ? normalized.filter((u) => String(u.name).toLowerCase().includes(q)) : normalized;
  }, [search, team]);

  const teamBuckets = useMemo(() => buildAggregateBuckets(teamUsers), [teamUsers]);

  const chartSeries = useMemo(() => {
    const buckets = canSeeTeam ? teamBuckets : myBuckets;
    return (buckets || []).map(buildBucket);
  }, [canSeeTeam, myBuckets, teamBuckets]);

  const [selectedBucketIndex, setSelectedBucketIndex] = useState(0);

  useEffect(() => {
    if (!chartSeries || chartSeries.length === 0) {
      setSelectedBucketIndex(0);
      return;
    }
    setSelectedBucketIndex((idx) => Math.min(Math.max(0, idx), chartSeries.length - 1));
  }, [chartSeries]);

  const selectedBucket = chartSeries[selectedBucketIndex] || null;

  const roleScopeLabel = (v) => {
    if (v === "sales") return t("pilotPerformance.list.filters.roleScopeSales");
    if (v === "recruiter") return t("pilotPerformance.list.filters.roleScopeRecruiter");
    return t("pilotPerformance.list.filters.roleScopeAll");
  };

  const showSalesSection = canSeeTeam ? roleScope !== "recruiter" : activityRole === "sales";
  const showRecruiterSection = canSeeTeam ? roleScope !== "sales" : activityRole === "recruiter";

  const salesMetrics = useMemo(
    () => [
      {
        key: "totalOpenRequirements",
        label: t("pilotPerformance.metrics.totalOpenRequirements", "Total Open Requirements"),
        color: CHART_NAVY,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "newOpenRequirements",
        label: t("pilotPerformance.metrics.newOpenRequirements", "New Open Requirements"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "coveredRequirements",
        label: t("pilotPerformance.metrics.coveredRequirements", "Covered Requirements"),
        color: CHART_MINT,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "profilesPresented",
        label: t("pilotPerformance.metrics.profilesPresented", "Profiles Presented"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "wonRequirements",
        label: t("pilotPerformance.metrics.wonRequirements", "Won Requirements"),
        color: CHART_MINT,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "offMarketOpportunities",
        label: t("pilotPerformance.metrics.offMarketOpportunities"),
        color: CHART_NAVY,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "referralOpportunities",
        label: t("pilotPerformance.metrics.referralOpportunities"),
        color: CHART_MINT,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "recruiterProfilesReceived",
        label: t("pilotPerformance.metrics.recruiterProfilesReceived"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "avgAsapSkillsScoreProposed",
        label: t("pilotPerformance.metrics.avgAsapSkillsScoreProposed"),
        color: CHART_LINE,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      },
      {
        key: "candidatesSubmitted",
        label: t("pilotPerformance.metrics.candidatesSubmitted"),
        color: CHART_NAVY,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "clientInterviews",
        label: t("pilotPerformance.metrics.clientInterviews"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "wins",
        label: t("pilotPerformance.metrics.wins"),
        color: CHART_MINT,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "conversionOppToInterview",
        label: t("pilotPerformance.metrics.conversionOppToInterview"),
        color: CHART_LINE,
        chart: "line",
        format: (v) => formatPercent(v)
      },
      {
        key: "conversionOppToWin",
        label: t("pilotPerformance.metrics.conversionOppToWin"),
        color: CHART_NAVY,
        chart: "line",
        format: (v) => formatPercent(v)
      }
    ],
    [t]
  );

  const recruiterMetrics = useMemo(
    () => [
      {
        key: "staffingNeedsToCover",
        label: t("pilotPerformance.metrics.staffingNeedsToCover"),
        color: CHART_NAVY,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "profilesProposed",
        label: t("pilotPerformance.metrics.profilesProposed"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "avgAtsScore",
        label: t("pilotPerformance.metrics.avgAtsScore"),
        color: CHART_LINE,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      },
      {
        key: "avgAsapSkillsScoreProposed",
        label: t("pilotPerformance.metrics.avgAsapSkillsScoreProposed"),
        color: CHART_MINT,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      },
      {
        key: "leadTimeOppToFirstProfile",
        label: t("pilotPerformance.metrics.leadTimeOppToFirstProfile"),
        color: CHART_NAVY_3,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      }
    ],
    [t]
  );

  const renderMetricChart = (metric) => {
    if (metric.chart === "line") {
      const isPercent = metric.key.includes("conversion");
      return (
        <MetricLineChart
          data={chartSeries}
          dataKey={metric.key}
          color={metric.color}
          selectedIndex={selectedBucketIndex}
          onSelect={setSelectedBucketIndex}
          valueFormatter={metric.format}
          yAxisFormatter={isPercent ? (v) => `${(normalizeRate(v) * 100).toFixed(0)}%` : undefined}
        />
      );
    }
    return (
      <MetricBarChart
        data={chartSeries}
        dataKey={metric.key}
        color={metric.color}
        selectedIndex={selectedBucketIndex}
        onSelect={setSelectedBucketIndex}
        valueFormatter={metric.format}
      />
    );
  };

  const renderMetricSummary = (metrics) => (
    <KpiStrip columns={{ base: 2, md: 3, xl: 4 }}>
      {metrics.map((metric) => (
        <KpiCard
          key={metric.key}
          label={metric.label}
          value={metric.format(selectedBucket?.[metric.key])}
        />
      ))}
    </KpiStrip>
  );

  const tableColumns = useMemo(() => {
    if (roleScope === "recruiter") {
      return [
        { key: "staffingNeedsToCover", label: t("pilotPerformance.metrics.staffingNeedsToCover") },
        { key: "profilesProposed", label: t("pilotPerformance.metrics.profilesProposed") },
        { key: "leadTimeOppToFirstProfile", label: t("pilotPerformance.metrics.leadTimeOppToFirstProfile") }
      ];
    }
    if (roleScope === "sales") {
      return [
        { key: "totalOpenRequirements", label: t("pilotPerformance.metrics.totalOpenRequirements", "Total Open Requirements") },
        { key: "profilesPresented", label: t("pilotPerformance.metrics.profilesPresented", "Profiles Presented") },
        { key: "wonRequirements", label: t("pilotPerformance.metrics.wonRequirements", "Won Requirements") }
      ];
    }
    return [
      { key: "totalOpenRequirements", label: t("pilotPerformance.metrics.totalOpenRequirements", "Total Open Requirements") },
      { key: "profilesPresented", label: t("pilotPerformance.metrics.profilesPresented", "Profiles Presented") },
      { key: "wonRequirements", label: t("pilotPerformance.metrics.wonRequirements", "Won Requirements") }
    ];
  }, [roleScope, t]);

  const teamTableColumns = useMemo(
    () => [
      { key: "user", label: t("pilotPerformance.list.table.user"), width: "22%" },
      { key: "role", label: t("pilotPerformance.list.table.role"), width: "16%" },
      ...tableColumns.map((column) => ({
        key: column.key,
        label: column.label,
        align: "right",
      })),
    ],
    [tableColumns, t]
  );

  const financialColumns = useMemo(
    () => [
      { key: "consultant", label: t("financialKpis.consultant"), width: "14%" },
      { key: "client", label: t("financialKpis.client"), width: "12%" },
      { key: "need", label: t("financialKpis.need"), width: "16%" },
      { key: "dailyRate", label: t("financialKpis.dailyRate"), width: "9%", align: "right" },
      { key: "costRate", label: t("financialKpis.costRate"), width: "9%", align: "right" },
      { key: "billableDaysActual", label: t("financialKpis.billableDaysActual"), width: "10%", align: "right" },
      { key: "revenue", label: t("financialKpis.revenue"), width: "10%", align: "right" },
      { key: "grossMargin", label: t("financialKpis.grossMargin"), width: "10%", align: "right" },
      { key: "grossMarginPercent", label: t("financialKpis.grossMarginPercent"), width: "9%", align: "right" },
    ],
    [t]
  );

  const portfolioColumns = useMemo(
    () => [
      { key: "user", label: t("portfolioAnalysis.user"), width: "20%" },
      { key: "consultants", label: t("portfolioAnalysis.consultants"), width: "11%", align: "right" },
      { key: "activeAssignments", label: t("portfolioAnalysis.activeAssignments"), width: "13%", align: "right" },
      { key: "totalRevenue", label: t("portfolioAnalysis.totalRevenue"), width: "14%", align: "right" },
      { key: "totalMargin", label: t("portfolioAnalysis.totalMargin"), width: "14%", align: "right" },
      { key: "avgMarginPercent", label: t("portfolioAnalysis.avgMarginPercent"), width: "14%", align: "right" },
      { key: "utilization", label: t("portfolioAnalysis.utilization"), width: "14%", align: "right" },
    ],
    [t]
  );

  const dashboardReturnTo = location.state?.returnTo || "/portal/dashboard";
  const showDashboardBackButton = Boolean(location.state?.fromDashboard);

  const activitySummaryLabel = loading
    ? t("pilotPerformance.list.summary.loading")
    : t("pilotPerformance.list.summary.myRange", {
        start: range.fromDate || "-",
        end: range.toDate || "-"
      });

  return (
    // Bleed past PortalLayout's padding so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="PE"
        eyebrow={t("pilotPerformance.list.eyebrow", "ASAP PILOT")}
        title={t("pilotPerformance.list.pageTitle")}
        actions={
          showDashboardBackButton ? (
            <Button
              variant="secondary"
              size="sm"
              h="32px"
              leftIcon={<ArrowBackIcon boxSize="11px" />}
              onClick={() => navigate(dashboardReturnTo)}
            >
              {t("pilotPerformance.list.backToDashboard")}
            </Button>
          ) : null
        }
      />

      <Box p="20px">
        {/* Filters */}
        <Panel mb="12px">
          <Box px="12px" py="12px">
            <Flex gap="12px" wrap="wrap" align="flex-end">
              <Box>
                <Text
                  textTransform="uppercase"
                  fontSize="10px"
                  fontWeight="800"
                  letterSpacing="0.055em"
                  color="portal.muted"
                  sx={{ marginBottom: "4px !important" }}
                >
                  {t("pilotPerformance.list.filters.bucket")}
                </Text>
                <Select
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value)}
                  w="160px"
                  sx={filterSelectSx}
                >
                  <option value="week">{t("pilotPerformance.list.filters.bucketWeek")}</option>
                  <option value="month">{t("pilotPerformance.list.filters.bucketMonth")}</option>
                  <option value="year">{t("pilotPerformance.list.filters.bucketYear")}</option>
                </Select>
              </Box>

              <Box>
                <Text
                  textTransform="uppercase"
                  fontSize="10px"
                  fontWeight="800"
                  letterSpacing="0.055em"
                  color="portal.muted"
                  sx={{ marginBottom: "4px !important" }}
                >
                  {t("pilotPerformance.list.filters.fromDate")}
                </Text>
                <Input
                  type="date"
                  value={range.fromDate}
                  onChange={(e) => setRange((r) => ({ ...r, fromDate: e.target.value }))}
                  h="31px"
                  w="160px"
                  fontSize="12px"
                  fontWeight="600"
                />
              </Box>

              <Box>
                <Text
                  textTransform="uppercase"
                  fontSize="10px"
                  fontWeight="800"
                  letterSpacing="0.055em"
                  color="portal.muted"
                  sx={{ marginBottom: "4px !important" }}
                >
                  {t("pilotPerformance.list.filters.toDate")}
                </Text>
                <Input
                  type="date"
                  value={range.toDate}
                  onChange={(e) => setRange((r) => ({ ...r, toDate: e.target.value }))}
                  h="31px"
                  w="160px"
                  fontSize="12px"
                  fontWeight="600"
                />
              </Box>

              {canSeeTeam ? (
                <Box>
                  <Text
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.055em"
                    color="portal.muted"
                    sx={{ marginBottom: "4px !important" }}
                  >
                    {t("pilotPerformance.list.filters.roleScope")}
                  </Text>
                  <Select
                    value={roleScope}
                    onChange={(e) => setRoleScope(e.target.value)}
                    w="180px"
                    sx={filterSelectSx}
                  >
                    <option value="all">{roleScopeLabel("all")}</option>
                    <option value="sales">{roleScopeLabel("sales")}</option>
                    <option value="recruiter">{roleScopeLabel("recruiter")}</option>
                  </Select>
                </Box>
              ) : null}

              {canSeeTeam ? (
                <Box>
                  <Text
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.055em"
                    color="portal.muted"
                    sx={{ marginBottom: "4px !important" }}
                  >
                    {t("pilotPerformance.list.filters.search")}
                  </Text>
                  <Input
                    placeholder={t("pilotPerformance.list.filters.searchPlaceholder")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    h="31px"
                    w="240px"
                    fontSize="12px"
                    fontWeight="600"
                  />
                </Box>
              ) : null}

              {canSeeTeam && userIds.length > 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  h="31px"
                  onClick={() => setUserIds([])}
                >
                  {t("pilotPerformance.list.clearDashboardFilter")}
                </Button>
              ) : null}

              <Button
                variant="primary"
                size="sm"
                h="31px"
                onClick={() => (canSeeTeam ? fetchTeam() : fetchMy())}
                isLoading={loading}
              >
                {t("pilotPerformance.list.filters.refresh")}
              </Button>
            </Flex>

            {canSeeTeam && userIds.length > 0 ? (
              <Text mt="10px" fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                {t("pilotPerformance.list.dashboardFilterNotice", {
                  count: userIds.length
                })}
              </Text>
            ) : null}
          </Box>
        </Panel>

        {/* Activity charts */}
        <Panel
          title={t("pilotPerformance.list.pageTitle")}
          actions={
            <Text fontSize="11px" fontWeight="700" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
              {activitySummaryLabel}
            </Text>
          }
          mb="12px"
        >
          {loading ? (
            <Center py="40px">
              <Spinner color="portal.primary" />
            </Center>
          ) : chartSeries.length === 0 ? (
            <EmptyState
              icon={<Icon as={FaChartLine} />}
              title={t("pilotPerformance.common.empty")}
              hint={t("pilotPerformance.list.emptyHint", "Adjust the period or filters to see activity metrics.")}
            />
          ) : (
            <Box p="12px">
              {showSalesSection ? (
                <>
                  <Text
                    textTransform="uppercase"
                    fontSize="10.5px"
                    fontWeight="900"
                    letterSpacing="0.055em"
                    color="portal.text"
                    sx={{ marginBottom: "10px !important" }}
                  >
                    {t("pilotPerformance.sections.sales")}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" mb="16px">
                    {salesMetrics.map((metric) => (
                      <MetricCard key={metric.key} title={metric.label}>
                        {renderMetricChart(metric)}
                      </MetricCard>
                    ))}
                  </SimpleGrid>
                </>
              ) : null}

              {showRecruiterSection ? (
                <>
                  <Text
                    textTransform="uppercase"
                    fontSize="10.5px"
                    fontWeight="900"
                    letterSpacing="0.055em"
                    color="portal.text"
                    sx={{ marginBottom: "10px !important" }}
                  >
                    {t("pilotPerformance.sections.recruiter")}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" mb="16px">
                    {recruiterMetrics.map((metric) => (
                      <MetricCard key={metric.key} title={metric.label}>
                        {renderMetricChart(metric)}
                      </MetricCard>
                    ))}
                  </SimpleGrid>
                </>
              ) : null}

              {selectedBucket ? (
                <>
                  <Divider my="16px" borderColor="portal.line" />
                  <Text
                    fontSize="13px"
                    fontWeight="800"
                    color="portal.text"
                    sx={{ marginBottom: "10px !important" }}
                  >
                    {t("pilotPerformance.detail.chart.selectedPeriod", { period: selectedBucket.label })}
                  </Text>
                  {showSalesSection && renderMetricSummary(salesMetrics)}
                  {showRecruiterSection && (
                    <Box mt={showSalesSection ? "10px" : "0"}>{renderMetricSummary(recruiterMetrics)}</Box>
                  )}
                </>
              ) : null}
            </Box>
          )}
        </Panel>

        {/* Team table */}
        {canSeeTeam ? (
          <Panel
            title={t("pilotPerformance.list.table.user")}
            count={
              loading
                ? t("pilotPerformance.list.summary.loading")
                : t("pilotPerformance.list.summary.totalUsers", {
                    count: teamUsers.length,
                    roleScope: roleScopeLabel(roleScope)
                  })
            }
            mb="12px"
          >
            <DataTable
              columns={teamTableColumns}
              rows={teamUsers}
              loading={loading}
              renderRow={(u) => {
                const clickable = Boolean(u.id);
                return (
                  <Tr
                    key={String(u.id ?? u.name)}
                    cursor={clickable ? "pointer" : "default"}
                    onClick={() => {
                      if (!clickable) return;
                      navigate(`/portal/asap-pilot/performance/${u.id}`);
                    }}
                  >
                    <Td fontWeight="700" color="portal.text">{u.name}</Td>
                    <Td>{u.role || "-"}</Td>
                    {tableColumns.map((column) => (
                      <Td key={column.key} textAlign="right">
                        {column.key === "leadTimeOppToFirstProfile"
                          ? formatNumber(u.summary[column.key], 1)
                          : formatNumber(u.summary[column.key], 0)}
                      </Td>
                    ))}
                  </Tr>
                );
              }}
              emptyState={
                <EmptyState
                  icon={<Icon as={FaUsers} />}
                  title={t("pilotPerformance.list.table.noItems")}
                />
              }
            />
          </Panel>
        ) : null}

        {/* Financial KPIs Section */}
        <Panel title={t("financialKpis.title")} mb="12px">
          {financialLoading ? (
            <Center py="32px">
              <Spinner color="portal.primary" />
            </Center>
          ) : !financialData || !financialData.assignments?.length ? (
            <EmptyState
              icon={<Icon as={FaCoins} />}
              title={t("financialKpis.empty")}
            />
          ) : (
            <Box p="12px">
              <KpiStrip columns={{ base: 2, md: 3, xl: 6 }} mb="12px">
                <KpiCard
                  label={t("financialKpis.summary.assignmentCount")}
                  value={financialData.summary.assignmentCount}
                />
                <KpiCard
                  label={t("financialKpis.summary.totalRevenue")}
                  value={`${financialData.summary.totalRevenue?.toLocaleString() ?? "—"} €`}
                />
                <KpiCard
                  label={t("financialKpis.summary.totalCost")}
                  value={`${financialData.summary.totalCost?.toLocaleString() ?? "—"} €`}
                />
                <KpiCard
                  label={t("financialKpis.summary.totalMargin")}
                  value={`${financialData.summary.totalMargin?.toLocaleString() ?? "—"} €`}
                />
                <KpiCard
                  label={t("financialKpis.summary.avgMarginPercent")}
                  value={`${financialData.summary.avgMarginPercent ?? 0}%`}
                />
                <KpiCard
                  label={t("financialKpis.summary.totalBillableDays")}
                  value={financialData.summary.totalBillableDays}
                />
              </KpiStrip>
              <Box borderWidth="1px" borderColor="portal.line" borderRadius="10px" overflow="hidden">
                <DataTable
                  columns={financialColumns}
                  rows={financialData.assignments}
                  renderRow={(a) => (
                    <Tr key={a.matchId}>
                      <Td fontWeight="700" color="portal.text">{a.consultantName}</Td>
                      <Td>{a.clientName}</Td>
                      <Td>{a.needTitle}</Td>
                      <Td textAlign="right">{a.dailyRate != null ? `${a.dailyRate} €` : "—"}</Td>
                      <Td textAlign="right">{a.costRate != null ? `${a.costRate} €` : "—"}</Td>
                      <Td textAlign="right">{a.billableDaysActual ?? "—"}</Td>
                      <Td textAlign="right">{a.revenue != null ? `${a.revenue.toLocaleString()} €` : "—"}</Td>
                      <Td textAlign="right" color={a.grossMargin != null && a.grossMargin >= 0 ? "green.600" : "red.500"}>
                        {a.grossMargin != null ? `${a.grossMargin.toLocaleString()} €` : "—"}
                      </Td>
                      <Td textAlign="right">{a.grossMarginPercent != null ? `${a.grossMarginPercent}%` : "—"}</Td>
                    </Tr>
                  )}
                />
              </Box>
            </Box>
          )}
        </Panel>

        {/* Portfolio Analysis Section */}
        {canSeeTeam && (
          <Panel title={t("portfolioAnalysis.title")}>
            {portfolioLoading ? (
              <Center py="32px">
                <Spinner color="portal.primary" />
              </Center>
            ) : !portfolioData || !portfolioData.users?.length ? (
              <EmptyState
                icon={<Icon as={FaChartLine} />}
                title={t("portfolioAnalysis.empty")}
              />
            ) : (
              <Box p="12px">
                <KpiStrip columns={{ base: 1, md: 2 }} mb="12px">
                  <KpiCard
                    label={t("portfolioAnalysis.companyMargin")}
                    value={portfolioData.companyAvgMarginPercent != null ? `${portfolioData.companyAvgMarginPercent}%` : "—"}
                  />
                  <KpiCard
                    label={t("portfolioAnalysis.companyUtilization")}
                    value={portfolioData.companyAvgUtilization != null ? `${portfolioData.companyAvgUtilization}%` : "—"}
                  />
                </KpiStrip>
                <Box borderWidth="1px" borderColor="portal.line" borderRadius="10px" overflow="hidden">
                  <DataTable
                    columns={portfolioColumns}
                    rows={portfolioData.users}
                    renderRow={(u) => (
                      <Tr key={u.userId}>
                        <Td fontWeight="700" color="portal.text">{u.firstName} {u.lastName}</Td>
                        <Td textAlign="right">{u.consultantCount}</Td>
                        <Td textAlign="right">{u.activeAssignments}</Td>
                        <Td textAlign="right">{u.totalRevenue != null ? `${u.totalRevenue.toLocaleString()} €` : "—"}</Td>
                        <Td textAlign="right" color={u.totalMargin != null && u.totalMargin >= 0 ? "green.600" : "red.500"}>
                          {u.totalMargin != null ? `${u.totalMargin.toLocaleString()} €` : "—"}
                        </Td>
                        <Td textAlign="right">{u.avgMarginPercent != null ? `${u.avgMarginPercent}%` : "—"}</Td>
                        <Td textAlign="right">{u.utilizationRate != null ? `${u.utilizationRate}%` : "—"}</Td>
                      </Tr>
                    )}
                  />
                </Box>
              </Box>
            )}
          </Panel>
        )}
      </Box>
    </Box>
  );
};

export default Performance;
